import type { ContentDetailsTestItem, ContentEvent, TaskResultData, UserTaskData } from '~/models/Report'
import type { GroupMember } from '~/models/Group'

export interface UserInfo {
  firstName?: string
  lastName?: string
  fullName?: string
}

export function resolveUserName(
  groupMember?: GroupMember,
  fallbackUser?: UserInfo
): string {
  if (groupMember) {
    if (groupMember.fullName) {
      return groupMember.fullName
    }
    if (groupMember.firstName || groupMember.lastName) {
      return `${groupMember.firstName ?? ''} ${groupMember.lastName ?? ''}`.trim()
    }
  }

  if (fallbackUser) {
    if (fallbackUser.fullName) {
      return fallbackUser.fullName
    }
    if (fallbackUser.firstName || fallbackUser.lastName) {
      return `${fallbackUser.firstName ?? ''} ${fallbackUser.lastName ?? ''}`.trim()
    }
  }

  return '\u00A0'
}

export function calculateTotalTasks(events: ContentEvent[], defaultCount = 5): number {
  let maxTaskCount = 0
  events.forEach((event) => {
    if (event.flytItemSetResults && event.flytItemSetResults.length > 0) {
      const maxIndex = Math.max(...event.flytItemSetResults.map(item => item.index))
      maxTaskCount = Math.max(maxTaskCount, maxIndex + 1)
    }
  })
  return maxTaskCount || defaultCount
}

export type TaskResult = TaskResultData

interface ParsedAnswerData {
  isPass?: boolean
  score?: number
  totalQuestions?: number
  items?: ContentDetailsTestItem[]
}

export function parseLatestAnswerData(latestAnswer: unknown): ParsedAnswerData | null {
  try {
    if (!latestAnswer) {
      return null
    }
    if (latestAnswer && typeof latestAnswer === 'object' && 'data' in (latestAnswer as any)) {
      const d = (latestAnswer as any).data
      if (typeof d === 'string') {
        return JSON.parse(d)
      }
      return d
    }
    if (typeof latestAnswer === 'string') {
      return JSON.parse(latestAnswer)
    }
    return null
  } catch {
    return null
  }
}

export function extractTaskResult(userTask: UserTaskData): TaskResult {
  if (!userTask) {
    return { isPass: null, score: null, totalQuestions: null, items: [] }
  }

  const items = userTask.items || []
  const totalScore = items.reduce((sum, item) => sum + item.score, 0)
  const totalPossible = items.reduce((sum, item) => sum + item.targetScore, 0)

  const score = userTask.score ?? totalScore
  const totalQuestions = userTask.totalQuestions ?? items.length
  const isPass = userTask.isPass ?? (totalScore > 0 && totalScore >= (totalPossible * 0.5))

  return { isPass, score, totalQuestions, items }
}

export type TaskCompletionStatus = 'completed' | 'started' | 'notStarted'
export type TaskFilterStatus = 'all' | TaskCompletionStatus

export function getTaskCompletionStatus(userTask: UserTaskData): TaskCompletionStatus {
  if (!userTask || !userTask.items || userTask.items.length === 0) {
    return 'notStarted'
  }

  const completedItems = userTask.items.filter(item =>
    item.result === 'CORRECT' || item.result === 'PARTIALLY_CORRECT' || item.result === 'INCORRECT'
  )

  if (completedItems.length === 0) {
    return 'notStarted'
  } else if (completedItems.length === userTask.items.length) {
    return 'completed'
  } else {
    return 'started'
  }
}
